import { useEffect, useState } from 'react';
import { X, Loader2, Sparkles, CheckCircle, XCircle, Wifi } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { settingsService } from '../../services/settings.service';
import type { LlmConfigInput, LlmProvider } from '../../services/settings.service';

interface LlmIntegrationFormProps {
  onClose: () => void;
}

interface FormData {
  anthropicApiKey: string;
  openaiApiKey: string;
  defaultModel: string;
  fallbackModel: string;
  model: string;
  maxTokens: number;
}

const ANTHROPIC_MODELS = [
  { value: 'claude-opus-5', label: 'claude-opus-5 (mais capaz, recomendado)' },
  { value: 'claude-sonnet-5', label: 'claude-sonnet-5 (equilibrado)' },
  { value: 'claude-haiku-4-5', label: 'claude-haiku-4-5 (barato)' },
];

const OPENAI_MODELS = [
  { value: 'gpt-5.6-sol', label: 'gpt-5.6-sol' },
  { value: 'gpt-5.6-terra', label: 'gpt-5.6-terra' },
  { value: 'gpt-5.6-luna', label: 'gpt-5.6-luna' },
];

const BOUNCE_MODELS = [
  { value: 'gpt-4o-mini', label: 'gpt-4o-mini (barato, recomendado)' },
  { value: 'gpt-4o', label: 'gpt-4o (mais preciso, mais caro)' },
  { value: 'gpt-5-mini', label: 'gpt-5-mini' },
  { value: 'gpt-5', label: 'gpt-5' },
];

/**
 * Um valor gravado no banco que nao esta na lista fixa (ex.: modelo
 * descontinuado por um provider) some silenciosamente do <select> — o
 * navegador cai na primeira opcao e o primeiro "Salvar" reescreve o valor
 * antigo com ela. Injetar o valor gravado como opcao extra torna essa troca
 * visivel em vez de silenciosa.
 */
function withStoredOption(
  options: { value: string; label: string }[],
  stored: string | null | undefined
): { value: string; label: string }[] {
  if (!stored || options.some((o) => o.value === stored)) {
    return options;
  }
  return [...options, { value: stored, label: `${stored} (valor gravado, fora da lista atual)` }];
}

export function LlmIntegrationForm({ onClose }: LlmIntegrationFormProps) {
  const qc = useQueryClient();
  const [testResult, setTestResult] = useState<{
    provider: LlmProvider;
    success: boolean;
    error?: string;
  } | null>(null);
  const [testingProvider, setTestingProvider] = useState<LlmProvider | null>(null);

  const { data: aiConfig, isLoading } = useQuery({
    queryKey: ['settings', 'ai'],
    queryFn: () => settingsService.getAi(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      anthropicApiKey: '',
      openaiApiKey: '',
      defaultModel: 'claude-opus-5',
      fallbackModel: '',
      model: 'gpt-4o-mini',
      maxTokens: 1000,
    },
  });

  useEffect(() => {
    if (aiConfig) {
      reset({
        anthropicApiKey: '',
        openaiApiKey: '',
        defaultModel: aiConfig.defaultModel,
        fallbackModel: aiConfig.fallbackModel ?? '',
        model: aiConfig.model,
        maxTokens: aiConfig.maxTokens,
      });
    }
  }, [aiConfig, reset]);

  const saveMutation = useMutation({
    mutationFn: (data: LlmConfigInput) => settingsService.saveAi(data),
    onSuccess: () => {
      toast.success('Configuração de IA salva');
      qc.invalidateQueries({ queryKey: ['settings', 'ai'] });
      setTestResult(null);
      onClose();
    },
    onError: () => toast.error('Erro ao salvar configuração de IA'),
  });

  const removeMutation = useMutation({
    mutationFn: (provider: LlmProvider) =>
      settingsService.saveAi(
        provider === 'anthropic' ? { anthropicApiKey: '' } : { openaiApiKey: '' }
      ),
    onSuccess: () => {
      toast.success('Chave removida');
      qc.invalidateQueries({ queryKey: ['settings', 'ai'] });
    },
    onError: () => toast.error('Erro ao remover a chave'),
  });

  async function onSubmit(data: FormData) {
    const payload: LlmConfigInput = {
      defaultModel: data.defaultModel,
      fallbackModel: data.fallbackModel || null,
      model: data.model,
      maxTokens: data.maxTokens,
    };
    // Campo em branco significa "nao mexer". Para apagar uma chave existe o
    // botao Remover, que envia '' explicitamente.
    if (data.anthropicApiKey.trim()) {
      payload.anthropicApiKey = data.anthropicApiKey.trim();
    }
    if (data.openaiApiKey.trim()) {
      payload.openaiApiKey = data.openaiApiKey.trim();
    }
    await saveMutation.mutateAsync(payload);
  }

  async function handleTest(provider: LlmProvider) {
    setTestingProvider(provider);
    setTestResult(null);
    try {
      const result = await settingsService.testAi(provider);
      setTestResult({ provider, ...result });
      if (result.success) {
        toast.success(`Chave da ${provider} válida!`);
      } else {
        toast.error(`Falha: ${result.error}`);
      }
    } catch {
      toast.error('Erro ao testar a chave');
    } finally {
      setTestingProvider(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-surface">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-ink-primary" />
            <h2 className="text-lg font-semibold text-ink-primary">Modelos de IA</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-subtle">
            <X className="w-5 h-5 text-ink-tertiary" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-ink-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
            <p className="text-sm text-ink-tertiary">
              As chaves ficam cifradas no banco, não em variáveis de ambiente. Depois de salvas elas
              nunca voltam para o navegador — para trocar, digite a nova; para apagar, use Remover.
            </p>

            <div className="space-y-2 border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-ink-secondary">
                  Anthropic API Key
                </label>
                <span className="text-xs text-ink-tertiary">
                  {aiConfig?.hasAnthropicKey ? 'Chave gravada' : 'Não configurada'}
                </span>
              </div>
              <input
                type="password"
                autoComplete="off"
                placeholder={
                  aiConfig?.hasAnthropicKey
                    ? 'Chave gravada — preencha para substituir'
                    : 'sk-ant-...'
                }
                {...register('anthropicApiKey')}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              />
              <div className="flex items-center justify-between">
                <a
                  href="https://console.anthropic.com/settings/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline text-accent"
                >
                  console.anthropic.com/settings/keys
                </a>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleTest('anthropic')}
                    disabled={!aiConfig?.hasAnthropicKey || testingProvider !== null}
                    title={
                      aiConfig?.hasAnthropicKey
                        ? 'Testar a chave Anthropic'
                        : 'Salve uma chave antes de testar'
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-secondary bg-subtle rounded-lg hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {testingProvider === 'anthropic' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Wifi className="w-3.5 h-3.5" />
                    )}
                    Testar
                  </button>
                  <button
                    type="button"
                    onClick={() => removeMutation.mutate('anthropic')}
                    disabled={!aiConfig?.hasAnthropicKey || removeMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium text-danger hover:bg-subtle rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Remover
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-2 border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-ink-secondary">
                  OpenAI API Key
                </label>
                <span className="text-xs text-ink-tertiary">
                  {aiConfig?.hasOpenaiKey ? 'Chave gravada' : 'Não configurada'}
                </span>
              </div>
              <input
                type="password"
                autoComplete="off"
                placeholder={
                  aiConfig?.hasOpenaiKey ? 'Chave gravada — preencha para substituir' : 'sk-...'
                }
                {...register('openaiApiKey')}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              />
              <div className="flex items-center justify-between">
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline text-accent"
                >
                  platform.openai.com/api-keys
                </a>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleTest('openai')}
                    disabled={!aiConfig?.hasOpenaiKey || testingProvider !== null}
                    title={
                      aiConfig?.hasOpenaiKey
                        ? 'Testar a chave OpenAI'
                        : 'Salve uma chave antes de testar'
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-secondary bg-subtle rounded-lg hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {testingProvider === 'openai' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Wifi className="w-3.5 h-3.5" />
                    )}
                    Testar
                  </button>
                  <button
                    type="button"
                    onClick={() => removeMutation.mutate('openai')}
                    disabled={!aiConfig?.hasOpenaiKey || removeMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium text-danger hover:bg-subtle rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Remover
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Modelo padrão
              </label>
              <select
                {...register('defaultModel')}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              >
                {withStoredOption(ANTHROPIC_MODELS, aiConfig?.defaultModel).map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Modelo de fallback
              </label>
              <select
                {...register('fallbackModel')}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              >
                <option value="">Nenhum</option>
                {withStoredOption(OPENAI_MODELS, aiConfig?.fallbackModel).map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Modelo para análise de bounces
              </label>
              <select
                {...register('model')}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              >
                {withStoredOption(BOUNCE_MODELS, aiConfig?.model).map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Tokens máximos por análise
              </label>
              <input
                type="number"
                {...register('maxTokens', {
                  valueAsNumber: true,
                  min: { value: 50, message: 'Mínimo 50' },
                  max: { value: 8000, message: 'Máximo 8000' },
                })}
                className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
              />
              {errors.maxTokens && (
                <p className="mt-1 text-xs text-danger">{errors.maxTokens.message}</p>
              )}
            </div>

            {testResult && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  testResult.success
                    ? 'bg-subtle text-accent border border-accent'
                    : 'bg-subtle text-danger border border-danger'
                }`}
              >
                {testResult.success ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                )}
                <span>
                  {testResult.success
                    ? `Chave da ${testResult.provider} válida!`
                    : `Falha (${testResult.provider}): ${testResult.error}`}
                </span>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-ink-secondary hover:bg-subtle rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-canvas bg-ink-primary rounded-lg hover:bg-ink-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
